import {
  ClockCircleOutlined,
  FileTextOutlined,
  LoadingOutlined,
  RightOutlined,
  DownOutlined,
} from "@ant-design/icons";
import {
  Divider,
  Col,
  Row,
  Typography,
  Timeline as AntTimeline,
  Card,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { getSchemeDisplayName } from "./schemeDisplay";

const { Text, Title } = Typography;

interface Props {
  data: any;
  isModalVisible?: boolean;
  handleCancel?: any;
  handleOpen?: any;
  handleSetDocumentUrl?: any;
  handleDownloadDocument?: any;
  downloadingDocumentUrl?: string;
  isDownloadingDocument?: boolean;
}

const MailTimelineContent: React.FC<Props> = ({
  data,
  handleOpen,
  handleDownloadDocument,
  isDownloadingDocument,
  downloadingDocumentUrl,
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (!data || data.length === 0) return null;

  return (
    <>
      {/* Header */}
      <Row
        align="middle"
        justify="space-between"
        style={{
          marginBottom: 20,
          paddingBottom: 12,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            Mail Timeline
          </Title>
        </Col>
      </Row>

      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: 8,
        }}
      >
        <AntTimeline>
          {data.map((item: any) => {
            const isExpanded = expandedId === item.id;

            return (
              <AntTimeline.Item key={item.id}>
                <Card
                  size="small"
                  bordered
                  style={{
                    borderRadius: 10,
                    boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                  }}
                  bodyStyle={{ padding: "16px 18px" }}
                >
                  {/* Status Row */}
                  <Row
                    align="middle"
                    justify="space-between"
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleExpand(item.id)}
                  >
                    <Space size={8}>
                      {isExpanded ? <DownOutlined /> : <RightOutlined />}
                      <Text strong style={{ fontSize: 15 }}>
                        {item.updatedStatus}
                      </Text>
                    </Space>

                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(item.updatedAt).format("DD MMM YYYY, hh:mm A")}
                    </Text>
                  </Row>

                  {/* Expanded Mail Details */}
                  {isExpanded && (
                    <>
                      <Divider style={{ margin: "14px 0" }} />

                      <Row gutter={[24, 16]}>
                        {item.schemeName && (
                          <Col span={12}>
                            <Text
                              type="secondary"
                              style={{ fontSize: 14, fontWeight: 700 }}
                            >
                              Scheme Name :-
                            </Text>
                            <br />
                            <Text strong>{getSchemeDisplayName(item)}</Text>
                          </Col>
                        )}

                        {item.sourceFromEmail && (
                          <Col span={12}>
                            <Text type="secondary">Source Email</Text>
                            <div>
                              <Text strong>{item.sourceFromEmail}</Text>
                            </div>
                          </Col>
                        )}

                        {item.mailSubject && (
                          <Col span={24}>
                            <Text type="secondary">Mail Subject</Text>
                            <div>
                              <Text strong>{item.mailSubject}</Text>
                            </div>
                          </Col>
                        )}

                        {/* {item.mailBody && (
                          <Col span={24}>
                            <Text type="secondary">Mail Body</Text>
                            <div
                              style={{
                                marginTop: 8,
                                padding: 14,
                                background: "#f9fafb",
                                borderRadius: 8,
                                border: "1px solid #f0f0f0",
                                whiteSpace: "pre-wrap",
                                fontSize: 13,
                              }}
                            >
                              {item.mailBody}
                            </div>
                          </Col>
                        )} */}
                        {item.mailBody && (
                          <Col span={24}>
                            <Text type="secondary" style={{ fontWeight: 500 }}>
                              Mail Content
                            </Text>

                            <div
                              style={{
                                marginTop: 12,
                                padding: 20,
                                background: "#ffffff",
                                borderRadius: 10,
                                border: "1px solid #f0f0f0",
                                fontSize: 13,
                                lineHeight: 1.7,
                              }}
                            >
                              {item.mailBody
                                .split("\n")
                                .filter((line: string) => line.trim() !== "")
                                .map((line: string, index: number) => {
                                  const trimmed = line.trim();

                                  // Bullet points
                                  if (trimmed.startsWith("·")) {
                                    return (
                                      <div
                                        key={index}
                                        style={{
                                          paddingLeft: 18,
                                          position: "relative",
                                          marginBottom: 6,
                                        }}
                                      >
                                        <span
                                          style={{
                                            position: "absolute",
                                            left: 0,
                                            color: "#1677ff",
                                          }}
                                        >
                                          •
                                        </span>
                                        {trimmed.replace("·", "")}
                                      </div>
                                    );
                                  }

                                  // Key-like rows (simple heuristic)
                                  if (
                                    trimmed.includes(":") &&
                                    trimmed.length < 120 &&
                                    !trimmed.startsWith("Disclaimer")
                                  ) {
                                    const [key, value] = trimmed.split(":");
                                    return (
                                      <div
                                        key={index}
                                        style={{ marginBottom: 6 }}
                                      >
                                        <Text strong>{key}:</Text>{" "}
                                        <Text type="secondary">{value}</Text>
                                      </div>
                                    );
                                  }

                                  // Disclaimer collapsed styling
                                  if (trimmed.startsWith("Disclaimer")) {
                                    return (
                                      <div
                                        key={index}
                                        style={{
                                          marginTop: 16,
                                          padding: 12,
                                          background: "#fafafa",
                                          fontSize: 12,
                                          color: "#8c8c8c",
                                          borderRadius: 6,
                                        }}
                                      >
                                        {trimmed}
                                      </div>
                                    );
                                  }

                                  // Normal paragraph
                                  return (
                                    <div
                                      key={index}
                                      style={{ marginBottom: 10 }}
                                    >
                                      {trimmed}
                                    </div>
                                  );
                                })}
                            </div>
                          </Col>
                        )}
                      </Row>
                    </>
                  )}

                  <Divider style={{ margin: "16px 0" }} />

                  {/* Bottom Details */}
                  <Row gutter={[32, 16]}>
                    <Col>
                      <Text type="secondary">Updated By</Text>
                      <div>
                        <Text strong>{item.updatedBy}</Text>
                      </div>
                    </Col>

                    {item.policyNumber && (
                      <Col>
                        <Text type="secondary">Policy No.</Text>
                        <div>
                          <Text strong>{item.policyNumber}</Text>
                        </div>
                      </Col>
                    )}

                    {item.intimatedAmount && (
                      <Col>
                        <Text type="secondary">Intimated Amount</Text>
                        <div>
                          <Text strong>₹{item.intimatedAmount}</Text>
                        </div>
                      </Col>
                    )}

                    {item.approvedAmount && (
                      <Col>
                        <Text type="secondary">Approved Amount</Text>
                        <div>
                          <Text strong style={{ color: "#389e0d" }}>
                            ₹{item.approvedAmount}
                          </Text>
                        </div>
                      </Col>
                    )}

                    {item.finalApprovedAmount && (
                      <Col>
                        <Text type="secondary">Final Approved</Text>
                        <div>
                          <Text strong style={{ color: "#0958d9" }}>
                            ₹{item.finalApprovedAmount}
                          </Text>
                        </div>
                      </Col>
                    )}

                    {item.documentUrl && (
                      <Col>
                        <Space size={16}>
                          <span
                            style={{
                              color: "#1677ff",
                              cursor: "pointer",
                              fontWeight: 500,
                            }}
                            onClick={() =>
                              handleOpen(item.visitId, "Mail", item.documentUrl)
                            }
                          >
                            <FileTextOutlined /> View
                          </span>

                          <span
                            style={{
                              color: "#13c2c2",
                              cursor: "pointer",
                              fontWeight: 500,
                            }}
                            onClick={() =>
                              handleDownloadDocument("Mail", item.documentUrl)
                            }
                          >
                            {isDownloadingDocument &&
                            downloadingDocumentUrl === item.documentUrl ? (
                              <LoadingOutlined />
                            ) : (
                              <>
                                <FileTextOutlined /> Download
                              </>
                            )}
                          </span>
                        </Space>
                      </Col>
                    )}
                  </Row>

                  {/* Optional Query */}
                  {isExpanded && item.queryRequirements && (
                    <>
                      <Divider style={{ margin: "14px 0" }} />
                      <Text type="secondary">Query</Text>
                      <div>
                        <Text strong>{item.queryRequirements}</Text>
                      </div>
                    </>
                  )}
                </Card>
              </AntTimeline.Item>
            );
          })}
        </AntTimeline>
      </div>
    </>
  );
};

export default MailTimelineContent;
